import { Component, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

import { LucideIconComponent } from '../../../shared/ui/lucide-icon/lucide-icon.component';
import { ModalService } from '../../../core/services/modal.service';
import { LoadService } from '../../../core/services/load.service';
import { AuthService } from '../../../core/services/auth.service';
import { ToastService } from '../../../shared/ui/toast/toast.service';
import { LoadWithView } from '../../../core/models/load.model';
import { Stop } from '../../../core/models/stop.model';
import { computeLoadLineItem } from '../../../shared/utilities/invoice-pricing';

import { ButtonComponent } from '../../../shared/ui/button/button.component';
import { NumberInputComponent } from '../../../shared/ui/forms/number-input/number-input.component';
import { FormFieldComponent } from '../../../shared/ui/forms/form-field/form-field.component';
import { InputComponent } from '../../../shared/ui/forms/input/input.component';
import { CheckboxComponent } from '../../../shared/ui/forms/checkbox/checkbox.component';
import { TextComponent } from '../../../shared/ui/typography/text/text.component';
import { HeadingComponent } from '../../../shared/ui/typography/heading/heading.component';
import { BadgeComponent } from '../../../shared/ui/badge/badge.component';

@Component({
  selector: 'app-edit-pricing-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LucideIconComponent,
    ButtonComponent,
    NumberInputComponent,
    FormFieldComponent,
    InputComponent,
    CheckboxComponent,
    TextComponent,
    HeadingComponent,
    BadgeComponent,
  ],
  templateUrl: './edit-pricing-modal.component.html',
})
export class EditPricingModalComponent {
  private readonly fb = inject(FormBuilder);
  private readonly loadService = inject(LoadService);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);
  private readonly modalService = inject(ModalService);

  readonly hasCustomActions = true;

  readonly load = (this.modalService.state() as any)?.data?.load as LoadWithView;
  readonly onSuccess = (this.modalService.state() as any)?.data?.onSuccess;

  readonly canEditBilling = this.authService.canEditBilling;
  readonly isSaving = signal(false);

  readonly form: FormGroup;

  // Dwell-time / detention auto-detection
  readonly detectedDetention = computed(() => {
    if (!this.load?.stops || this.load.stops.length === 0) return null;

    let totalExcessHours = 0;
    const stopDwells: { stopNumber: number; facility: string; excessHours: number }[] = [];

    for (const stop of this.load.stops as Stop[]) {
      let excess = 0;
      if (stop.arrivedAt && stop.departedAt) {
        const start = stop.arrivedAt.toMillis ? stop.arrivedAt.toMillis() : new Date(stop.arrivedAt as any).getTime();
        const end = stop.departedAt.toMillis ? stop.departedAt.toMillis() : new Date(stop.departedAt as any).getTime();
        const dwellMinutes = Math.max(0, Math.round((end - start) / 60000));
        excess = Math.max(0, (dwellMinutes - 120) / 60);
      } else if (stop.detentionHours) {
        excess = stop.detentionHours;
      }

      if (excess > 0) {
        const roundedExcess = Number(excess.toFixed(2));
        totalExcessHours += roundedExcess;
        stopDwells.push({
          stopNumber: stop.stopNumber ?? 0,
          facility: stop.companyName || stop.address?.city || `Stop #${stop.stopNumber ?? 0}`,
          excessHours: roundedExcess,
        });
      }
    }

    if (totalExcessHours <= 0) return null;

    const roundedTotalHours = Number(totalExcessHours.toFixed(2));
    const suggestedRate = 50; // Standard $50/hr
    const suggestedTotal = Number((roundedTotalHours * suggestedRate).toFixed(2));

    return {
      totalHours: roundedTotalHours,
      suggestedRate,
      suggestedTotal,
      stops: stopDwells,
    };
  });

  // Lumper receipts auto-detection from stops & load documents
  readonly detectedLumperReceipts = computed(() => {
    const receipts: {
      id: string;
      label: string;
      url: string;
      fee?: number | null;
      paidBy?: string | null;
      paymentMethod?: string | null;
      stopNumber?: number | null;
      facility?: string;
    }[] = [];

    if (!this.load) return receipts;

    // 1. Check all stops for lumper receipt documents and fees
    if (this.load.stops && Array.isArray(this.load.stops)) {
      this.load.stops.forEach((stop: Stop, idx: number) => {
        const stopNum = stop.stopNumber ?? (idx + 1);
        const facility = stop.companyName || (stop.address?.city ? `${stop.address.city}, ${stop.address.state || ''}` : `Stop #${stopNum}`);

        const stopDoc = stop.documents?.lumperReceipt;
        if (stopDoc?.url) {
          receipts.push({
            id: `stop-${stopNum}`,
            label: `Stop #${stopNum} Lumper Receipt`,
            url: stopDoc.url,
            fee: stop.lumperFee,
            paidBy: stop.lumperPaidBy,
            paymentMethod: stop.lumperPaymentMethod,
            stopNumber: stopNum,
            facility,
          });
        }
      });
    }

    // 2. Check parent load documents
    const loadDocs = (this.load as any)?.documents;
    if (loadDocs?.lumperReceipt?.url) {
      if (!receipts.some(r => r.url === loadDocs.lumperReceipt.url)) {
        receipts.push({
          id: 'load-doc',
          label: 'Load-Level Lumper Receipt',
          url: loadDocs.lumperReceipt.url,
        });
      }
    }

    // 3. Check company view documents
    const viewDocs = (this.load as any)?.view?.documents;
    if (viewDocs?.lumperReceipt?.url) {
      if (!receipts.some(r => r.url === viewDocs.lumperReceipt.url)) {
        receipts.push({
          id: 'view-doc',
          label: 'Company View Lumper Receipt',
          url: viewDocs.lumperReceipt.url,
        });
      }
    }

    return receipts;
  });

  constructor() {
    const view = this.load?.view;
    const pricing = view?.pricing || {};
    const bookedAtRate = (view as any)?.bookedAtRate;

    const detentionHours = pricing.detentionHours ?? (pricing.detention ? pricing.detention / (pricing.detentionRatePerHour || 50) : 0);
    const detentionRate = pricing.detentionRatePerHour ?? 50;
    const detentionTotal = pricing.detention ?? (detentionHours * detentionRate);

    // Check detected lumper receipts for smart defaults
    const detectedReceipts = this.detectedLumperReceipts();
    let initialLumperUrl = pricing.lumperReceiptUrl ?? '';
    let initialLumperFee = pricing.lumper ?? 0;

    if (!initialLumperUrl && detectedReceipts.length > 0) {
      if (detectedReceipts.length === 1) {
        initialLumperUrl = detectedReceipts[0].url;
        if (!initialLumperFee && detectedReceipts[0].fee) {
          initialLumperFee = detectedReceipts[0].fee;
        }
      } else {
        initialLumperUrl = detectedReceipts.map(r => r.url).join(', ');
        const sumFees = detectedReceipts.reduce((sum, r) => sum + (Number(r.fee) || 0), 0);
        if (!initialLumperFee && sumFees > 0) {
          initialLumperFee = sumFees;
        }
      }
    }

    this.form = this.fb.group({
      baseRate: [pricing.baseRate ?? bookedAtRate ?? 0, [Validators.required, Validators.min(0)]],
      detentionHours: [detentionHours, [Validators.min(0)]],
      detentionRatePerHour: [detentionRate, [Validators.min(0)]],
      detention: [detentionTotal, [Validators.min(0)]],
      layover: [pricing.layover ?? 0, [Validators.min(0)]],
      lumper: [initialLumperFee, [Validators.min(0)]],
      lumperReceiptUrl: [initialLumperUrl],
      lumperReceiptWaived: [Boolean(pricing.lumperReceiptWaived)],
      fuelSurcharge: [pricing.fuelSurcharge ?? 0, [Validators.min(0)]],
      otherCharges: [pricing.otherCharges ?? 0, [Validators.min(0)]],
      otherChargesDescription: [pricing.otherChargesDescription ?? ''],
      deductions: [pricing.deductions ?? 0, [Validators.min(0)]],
      deductionsDescription: [pricing.deductionsDescription ?? ''],
    });

    if (!this.canEditBilling()) {
      this.form.disable();
    }
  }

  attachLumperReceipt(receipt: { url: string; fee?: number | null }): void {
    this.form.patchValue({ lumperReceiptUrl: receipt.url });
    if (receipt.fee && Number(this.form.get('lumper')?.value || 0) === 0) {
      this.form.patchValue({ lumper: receipt.fee });
    }
    this.toastService.show('info', 'Lumper receipt attached to invoice');
  }

  attachAllLumperReceipts(): void {
    const receipts = this.detectedLumperReceipts();
    if (receipts.length === 0) return;

    const urls = receipts.map(r => r.url).join(', ');
    const totalFee = receipts.reduce((sum, r) => sum + (Number(r.fee) || 0), 0);

    this.form.patchValue({ lumperReceiptUrl: urls });
    if (totalFee > 0 && Number(this.form.get('lumper')?.value || 0) === 0) {
      this.form.patchValue({ lumper: totalFee });
    }
    this.toastService.show('info', `All ${receipts.length} lumper receipts attached`);
  }

  viewReceipt(url: string, label: string = 'Lumper Receipt'): void {
    if (!url) return;
    const isPdf = url.toLowerCase().includes('.pdf');
    import('../../../shared/ui/document-viewer-modal/document-viewer-modal.component').then(
      ({ DocumentViewerModalComponent }) => {
        this.modalService.open(DocumentViewerModalComponent, {
          title: label,
          subtitle: 'View and download receipt',
          icon: 'FileText',
          size: 'xl',
          data: {
            url,
            documentType: isPdf ? 'pdf' : 'image',
          },
        });
      }
    );
  }

  openReceiptExternal(url: string): void {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  // Recalculate detention total when hours or rate changes
  onDetentionHoursOrRateChange(): void {
    const hours = Number(this.form.get('detentionHours')?.value || 0);
    const rate = Number(this.form.get('detentionRatePerHour')?.value || 0);
    const calculatedDetention = Number((hours * rate).toFixed(2));
    this.form.patchValue({ detention: calculatedDetention }, { emitEvent: false });
  }

  applyDetectedDetention(): void {
    const detected = this.detectedDetention();
    if (!detected) return;

    this.form.patchValue({
      detentionHours: detected.totalHours,
      detentionRatePerHour: detected.suggestedRate,
      detention: detected.suggestedTotal,
    });
    this.toastService.show(
      'success',
      `Applied ${detected.totalHours} hrs detention @ $${detected.suggestedRate}/hr ($${detected.suggestedTotal})`
    );
  }

  get isTonu(): boolean {
    return this.load?.view?.tonuDecision?.status === 'eligible';
  }

  get total(): number {
    const vals = this.form.getRawValue();
    const pricingInput = {
      baseRate: Number(vals.baseRate || 0),
      detention: Number(vals.detention || 0),
      detentionHours: Number(vals.detentionHours || 0),
      layover: Number(vals.layover || 0),
      lumper: Number(vals.lumper || 0),
      fuelSurcharge: Number(vals.fuelSurcharge || 0),
      otherCharges: Number(vals.otherCharges || 0),
      deductions: Number(vals.deductions || 0),
      tonuDecision: this.load?.view?.tonuDecision,
    };
    return computeLoadLineItem(pricingInput).total;
  }

  close() {
    this.modalService.close();
  }

  async save() {
    if (this.form.invalid || !this.canEditBilling()) return;

    this.isSaving.set(true);
    const vals = this.form.getRawValue();

    try {
      const companyId = this.authService.companyId() || this.load.carrierId || this.load.participantCompanyIds?.[0];
      if (!companyId) throw new Error('Cannot determine company');

      const existingPricing = this.load.view?.pricing || {};

      const updatedPricing = {
        ...existingPricing,
        baseRate: Number(vals.baseRate || 0),
        detention: Number(vals.detention || 0),
        detentionHours: Number(vals.detentionHours || 0),
        detentionRatePerHour: Number(vals.detentionRatePerHour || 0),
        layover: Number(vals.layover || 0),
        lumper: Number(vals.lumper || 0),
        lumperReceiptUrl: vals.lumperReceiptUrl?.trim() || null,
        lumperReceiptWaived: Boolean(vals.lumperReceiptWaived),
        fuelSurcharge: Number(vals.fuelSurcharge || 0),
        otherCharges: Number(vals.otherCharges || 0),
        otherChargesDescription: vals.otherChargesDescription?.trim() || null,
        deductions: Number(vals.deductions || 0),
        deductionsDescription: vals.deductionsDescription?.trim() || null,
        totalAmount: this.total,
      };

      const viewPatch = {
        pricing: updatedPricing,
      };

      await firstValueFrom(
        this.loadService.updateCompanyView(this.load.id, companyId, viewPatch)
      );

      if (this.load) {
        this.load.view = {
          ...(this.load.view || ({ loadId: this.load.id, companyId } as any)),
          ...viewPatch,
        };
      }

      this.toastService.show('success', 'Pricing and accessorials updated successfully');
      this.modalService.close();
      this.onSuccess?.(viewPatch);
    } catch (err: any) {
      console.error('Failed to update pricing:', err);
      this.toastService.show('error', err.message || 'Failed to update pricing');
    } finally {
      this.isSaving.set(false);
    }
  }
}
